import React from 'react';
import { OverlayPanel } from '../ui/OverlayPanel';

interface LocationNPC {
  id: string;
  name: string;
  title: string;
  portrait: string;
  dialogue?: {
    greeting: string;
    farewell: string;
  };
  questStatus?: 'available' | 'active' | 'complete' | 'none';
  questId?: string;
}

interface DockedPanelProps {
  islandName: string;
  islandId: string;
  isOpen: boolean;
  onClose: () => void;
  onUndock: () => void;
  onOpenShop: () => void;
  onOpenShipyard: () => void;
  onOpenCourier: () => void;
  onOpenTavern: () => void;
  onOpenBank: () => void;
  onUseDockServices: () => void;
  onOpenResearch?: () => void;
  onOpenTerritory?: () => void;
  onOpenFleet?: () => void;
  onTalkToNPC?: (npcId: string, npcName: string) => void;
  hasShop: boolean;
  hasShipyard: boolean;
  hasCourier: boolean;
  hasTavern: boolean;
  hasDockServices: boolean;
  isHarborOffice: boolean;
  isUndocking?: boolean;
  canResearch?: boolean;
  canClaimTerritory?: boolean;
  npcs?: LocationNPC[];
}

// Consistent styling constants
const SPACING = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
};

const FONT = {
  family: '"Sometype Mono", monospace',
  sizeSmall: 11,
  sizeMedium: 12,
  sizeLarge: 13,
  weightNormal: 400,
  weightBold: 700,
};

const COLORS = {
  text: '#e6edf3',
  textSecondary: '#8b949e',
  textTertiary: '#94a3b8',
  bgPrimary: '#0d1117',
  bgSecondary: '#0f172a',
  bgTertiary: '#0b1220',
  border: '#30363d',
  borderLight: '#1f2937',
  accentGreen: '#22c55e',
  accentGreenDark: '#059669',
  accentBlue: '#1f6feb',
  accentOrange: '#f59e0b',
  accentGold: '#b8860b',
  accentPurple: '#8b5cf6',
  accentIndigo: '#6366f1',
  accentRed: '#dc2626',
  accentRedDark: '#b91c1c',
  accentTeal: '#2a9d8f',
  accentOrangeDark: '#ea580c',
};

const buttonBaseStyle = {
  width: '100%' as const,
  padding: `${SPACING.sm}px ${SPACING.md}px`,
  border: '1px solid rgba(255,255,255,0.08)',
  color: COLORS.text,
  fontSize: FONT.sizeMedium,
  fontFamily: FONT.family,
  cursor: 'pointer' as const,
  textAlign: 'left' as const,
  textTransform: 'uppercase' as const,
  borderRadius: 0,
  fontWeight: FONT.weightNormal,
  transition: 'opacity 0.2s',
};

export const DockedPanel: React.FC<DockedPanelProps> = ({
  islandName,
  islandId,
  isOpen,
  onClose,
  onUndock,
  onOpenShop,
  onOpenShipyard,
  onOpenCourier,
  onOpenTavern,
  onOpenBank,
  onUseDockServices,
  onOpenResearch,
  onOpenTerritory,
  onOpenFleet,
  onTalkToNPC,
  hasShop,
  hasShipyard,
  hasCourier,
  hasTavern,
  hasDockServices,
  isHarborOffice,
  isUndocking = false,
  canResearch = false,
  canClaimTerritory = false,
  npcs = [],
}) => {
  // Handle NPC interaction with quest tracking
  const handleNPCInteraction = (npc: LocationNPC) => {
    if (onTalkToNPC) {
      onTalkToNPC(npc.id, npc.name);
    }
  };
  return (
    <OverlayPanel
      isOpen={isOpen}
      onClose={onClose}
      title={`${islandName.toUpperCase()}`}
      draggable
      storageKey="dockedPanelPosition"
      defaultPosition={{ x: 180, y: 200 }}
      clampToViewport
      closeOnEscape={false}
      closeOnBackdrop={false}
      zIndex={900}
      width={340}
    >
      {/* Available Actions */}
      <div style={{ padding: SPACING.md, paddingTop: SPACING.sm, display: 'flex', flexDirection: 'column', gap: SPACING.sm }}>
        {/* Quest Givers Section */}
        {npcs && npcs.some(npc => npc.questStatus === 'available') && (
          <>
            <div style={{ color: '#fbbf24', fontSize: FONT.sizeSmall, marginBottom: SPACING.sm, textTransform: 'uppercase', letterSpacing: 1, fontFamily: FONT.family }}>
              [*] Quest Givers
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING.sm, marginBottom: SPACING.md }}>
              {npcs.filter(npc => npc.questStatus === 'available').map((npc) => (
                <button
                  key={npc.id}
                  onClick={() => {
                    handleNPCInteraction(npc);
                    onClose();
                  }}
                  style={{
                    ...buttonBaseStyle,
                    backgroundColor: '#f59e0b',
                    fontWeight: FONT.weightBold,
                  }}
                >
                  [TALK TO {npc.name.toUpperCase()}]
                </button>
              ))}
            </div>
          </>
        )}

        {/* Active Quest NPCs Section */}
        {npcs && npcs.some(npc => npc.questStatus === 'active') && (
          <>
            <div style={{ color: '#60a5fa', fontSize: FONT.sizeSmall, marginBottom: SPACING.sm, textTransform: 'uppercase', letterSpacing: 1, fontFamily: FONT.family }}>
              [+] Active Quest NPCs
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING.sm, marginBottom: SPACING.md }}>
              {npcs.filter(npc => npc.questStatus === 'active').map((npc) => (
                <button
                  key={npc.id}
                  onClick={() => {
                    handleNPCInteraction(npc);
                    onClose();
                  }}
                  style={{
                    ...buttonBaseStyle,
                    backgroundColor: '#3b82f6',
                    fontWeight: FONT.weightBold,
                  }}
                >
                  [TALK TO {npc.name.toUpperCase()}]
                </button>
              ))}
            </div>
          </>
        )}

        {/* Completed Quest NPCs Section */}
        {npcs && npcs.some(npc => npc.questStatus === 'complete') && (
          <>
            <div style={{ color: '#34d399', fontSize: FONT.sizeSmall, marginBottom: SPACING.sm, textTransform: 'uppercase', letterSpacing: 1, fontFamily: FONT.family }}>
              [X] Completed Quests
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING.sm, marginBottom: SPACING.md }}>
              {npcs.filter(npc => npc.questStatus === 'complete').map((npc) => (
                <button
                  key={npc.id}
                  onClick={() => {
                    handleNPCInteraction(npc);
                    onClose();
                  }}
                  style={{
                    ...buttonBaseStyle,
                    backgroundColor: '#10b981',
                  }}
                >
                  [TALK TO {npc.name.toUpperCase()}]
                </button>
              ))}
            </div>
          </>
        )}

        {/* Other NPCs Section */}
        {npcs && npcs.some(npc => !npc.questStatus || npc.questStatus === 'none') && (
          <>
            <div style={{ color: COLORS.textSecondary, fontSize: FONT.sizeSmall, marginBottom: SPACING.sm, textTransform: 'uppercase', letterSpacing: 1, fontFamily: FONT.family }}>
              People
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING.sm, marginBottom: SPACING.md }}>
              {npcs.filter(npc => !npc.questStatus || npc.questStatus === 'none').map((npc) => (
                <button
                  key={npc.id}
                  onClick={() => {
                    handleNPCInteraction(npc);
                    onClose();
                  }}
                  style={{
                    ...buttonBaseStyle,
                    backgroundColor: COLORS.accentTeal,
                  }}
                >
                  [TALK TO {npc.name.toUpperCase()}]
                </button>
              ))}
            </div>
          </>
        )}
        <div style={{ color: COLORS.textSecondary, fontSize: FONT.sizeSmall, marginBottom: SPACING.sm, textTransform: 'uppercase', letterSpacing: 1, fontFamily: FONT.family }}>
          Available Services
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING.sm, maxHeight: '400px', overflowY: 'auto' }}>
          {hasDockServices && (
            <button
              onClick={onUseDockServices}
              style={{
                ...buttonBaseStyle,
                backgroundColor: COLORS.accentTeal,
              }}
            >
              [DOCK SERVICES]
            </button>
          )}
          {hasCourier && (
            <button
              onClick={onOpenCourier}
              style={{
                ...buttonBaseStyle,
                backgroundColor: isHarborOffice ? COLORS.accentPurple : COLORS.accentOrange,
              }}
            >
              {isHarborOffice ? '[REGULATED BOARD]' : '[COURIER BOARD]'}
            </button>
          )}
          {hasShop && (
            <button
              onClick={onOpenShop}
              style={{
                ...buttonBaseStyle,
                backgroundColor: '#238636',
              }}
            >
              [TRADING POST]
            </button>
          )}
          {hasShipyard && (
            <button
              onClick={() => {
                onOpenShipyard();
                onClose();
              }}
              style={{
                ...buttonBaseStyle,
                backgroundColor: COLORS.accentBlue,
              }}
            >
              [SHIPYARD]
            </button>
          )}
          {hasTavern && (
            <button
              onClick={() => {
                onOpenTavern();
                onClose();
              }}
              style={{
                ...buttonBaseStyle,
                backgroundColor: COLORS.accentGold,
              }}
            >
              [TAVERN]
            </button>
          )}
          {(hasShop || hasShipyard || hasTavern || isHarborOffice) && (
            <button
              onClick={() => {
                onOpenBank();
                onClose();
              }}
              style={{
                ...buttonBaseStyle,
                backgroundColor: COLORS.accentGreenDark,
              }}
            >
              [MERCHANT VAULT]
            </button>
          )}
          {canResearch && onOpenResearch && (
            <button
              onClick={() => {
                onOpenResearch();
                onClose();
              }}
              style={{
                ...buttonBaseStyle,
                backgroundColor: COLORS.accentIndigo,
              }}
            >
              [RESEARCH LAB]
            </button>
          )}
          {canClaimTerritory && onOpenTerritory && (
            <button
              onClick={() => {
                onOpenTerritory();
                onClose();
              }}
              style={{
                ...buttonBaseStyle,
                backgroundColor: COLORS.accentOrangeDark,
              }}
            >
              [CLAIM TERRITORY]
            </button>
          )}
          {onOpenFleet && (
            <button
              onClick={onOpenFleet}
              style={{
                ...buttonBaseStyle,
                backgroundColor: COLORS.accentTeal,
              }}
            >
              [FLEET MANAGEMENT]
            </button>
          )}
        </div>
      </div>

      {/* Undock */}
      <div style={{ padding: `0 ${SPACING.md}px ${SPACING.lg}px ${SPACING.md}px` }}>
        <button
          onClick={onUndock}
          disabled={isUndocking}
          style={{
            width: '100%',
            padding: `${SPACING.sm + 1}px ${SPACING.md}px`,
            backgroundColor: COLORS.accentRed,
            border: `1px solid ${COLORS.accentRedDark}`,
            color: COLORS.text,
            fontSize: FONT.sizeLarge,
            fontFamily: FONT.family,
            fontWeight: FONT.weightBold,
            cursor: 'pointer',
            opacity: isUndocking ? 0.85 : 1,
            textTransform: 'uppercase',
            borderRadius: 0,
            transition: 'opacity 0.2s',
          }}
        >
          {isUndocking ? 'PREPARING TO SET SAIL...' : '[UNDOCK & SET SAIL]'}
        </button>
      </div>
    </OverlayPanel>
  );
};
